/**
 * Workflows + intake — production pages (WP6e / #46629).
 *
 * Extracted from define.tsx so that file stays under the ~1,000-line cap
 * (#46660). Controls/Policies scaffolds remain in define.tsx for WP6f/g.
 * Product actions use verificationDefine contracts for Demo and Product
 * dogfood. No Prototype toasts.
 */

import { useState, type ChangeEvent, type MouseEvent } from "react";
import { formatDateTime } from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { activity } from "@excellent/ui/api/contracts/activity";
import { intakeOracleIsIndependent } from "@excellent/core/verification-workflow-intake";
import {
  projectGoldenVectorWorkflowsUiOutcome,
  resolveVerificationReasonCodePrecedence,
  type GoldenVectorWorkflowsUiOutcome,
} from "@excellent/core/verification-domain-schemas";
import { environmentOfId } from "../env";
import { href } from "../nav";
import { ActionMenu, PageActions, ScopeTabs, ToneBadge } from "../ui";
import {
  PageFrame,
  PageLink,
  PagePending,
  type VerificationDetailPageProps,
  type VerificationPageProps,
} from "./page-frame";
import { verificationCanonicalClients } from "../api/canonical-clients";
import goldenVectorRegistry from "../../../../docs/verification/v2/golden-vectors/registry.json";
import {
  ACTION,
  BIND_PREREQ,
  Btn,
  environmentFromWorkflowId,
  errorMessage,
  STATUS_LABEL,
  STATUS_TONE,
  workflowHref,
} from "./workflows-contracts";
import { useWorkflowActions, type WorkflowRow } from "./workflows-actions";
import { CheckResultProof } from "./check-result-proof";
import { GoldenVectorReasonCard } from "./workflows-golden-vector-reason-card";
import { RequiredStatusRenderingStrip } from "./workflows-required-status-strip";
export { WorkflowNewPage } from "./workflow-new-page";

type GoldenVectorFinding = {
  reasonCode: string;
  fixturePath: string;
  status: string;
  result: string | null;
  uiOutcome: GoldenVectorWorkflowsUiOutcome;
  summary: string;
};

type WorkflowProofBasis = {
  observation: string;
  observationReviewId: string;
  project: string;
  projectRoot: string;
  check: string;
};

function parseWorkflowProofBasis(evidence: string): WorkflowProofBasis | null {
  const match = evidence.match(
    /^(.*?)\s+Observation review:\s*(.*?)\.\s+Project context confirmed:\s*(.*?)\s+\((.*?)\)\.\s+Proposed independent check:\s*(.*)$/s,
  );
  if (!match) return null;
  const [, observation, observationReviewId, project, projectRoot, check] = match;
  if (!observation || !observationReviewId || !project || !projectRoot || !check) return null;
  return { observation, observationReviewId, project, projectRoot, check };
}

function normalizeReasonCodes(reason: string | undefined): string[] {
  return [...new Set((reason ?? "").split(",").map((code) => code.trim()).filter(Boolean))];
}

function goldenVectorFinding(reason: string | undefined): GoldenVectorFinding | null {
  const reasonCodes = normalizeReasonCodes(reason);
  if (reasonCodes.length === 0) return null;
  if (!reasonCodes.every((code) => goldenVectorRegistry.requiredReasonCodes.includes(code))) return null;
  const winner = resolveVerificationReasonCodePrecedence(reasonCodes);
  if (!goldenVectorRegistry.requiredReasonCodes.includes(winner)) return null;
  const fixturePath = goldenVectorRegistry.reasonCodeFixtureAliases[
    winner as keyof typeof goldenVectorRegistry.reasonCodeFixtureAliases
  ];
  const vector = goldenVectorRegistry.vectors.find(
    (candidate) =>
      candidate.expected.reasonCode === winner ||
      (candidate.fixture.source === fixturePath && candidate.expected.reasonCode === winner),
  );
  const status = vector?.expected.status ?? null;
  if (!fixturePath || !vector || !status) return null;
  const uiOutcome = projectGoldenVectorWorkflowsUiOutcome({
    reasonCode: winner,
    status,
    result: vector.expected.result,
  });
  return {
    reasonCode: winner,
    fixturePath,
    status,
    result: vector?.expected.result ?? null,
    uiOutcome,
    summary:
      reasonCodes.length > 1
        ? `Golden-vector fixture ${fixturePath} expects ${status} for ${winner}; remediation ${uiOutcome.remediation}; precedence resolved ${reasonCodes.join(" + ")}.`
        : `Golden-vector fixture ${fixturePath} expects ${status} for ${winner}; remediation ${uiOutcome.remediation}.`,
  };
}

export function WorkflowsPage({ onNavigate, reason }: VerificationPageProps & { reason?: string }) {
  const { environment, ready, workflowsApi, workflows, admitWorkflow } = useWorkflowActions();
  const [composing, setComposing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [scope, setScope] = useState<"admitted" | "waiting" | "rejected">("admitted");
  const reasonFinding = goldenVectorFinding(reason);

  const admitted = workflows.filter((c) => c.status === "admitted");
  const pending = workflows.filter((c) => c.status === "needs-oracle" || c.status === "draft");
  const rejected = workflows.filter((c) => c.status === "reject");
  const list = scope === "admitted" ? admitted : scope === "waiting" ? pending : rejected;
  const empty =
    scope === "admitted"
      ? { title: "No admitted workflows", body: "Nothing has passed the independent-oracle test yet." }
      : scope === "waiting"
        ? { title: "Nothing waiting", body: "Every proposed workflow has an independent oracle." }
        : { title: "None refused", body: "Refused workflows stay on the record here, with the reason." };

  const connectDisabled = !environment;
  const connectTitle = !environment ? "Load the active Environment first" : undefined;

  if (!ready) {
    return (
      <PageFrame page="workflows" eyebrow="Define" title="Workflows">
        <PagePending title="Environment unavailable">The active Environment must load before workflows can load.</PagePending>
      </PageFrame>
    );
  }

  if (workflowsApi.isLoading && !workflowsApi.data) {
    return (
      <PageFrame page="workflows" eyebrow="Define" title="Workflows">
        <PagePending title="Loading workflows">Fetching workflow candidates for this environment.</PagePending>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      page="workflows"
      eyebrow="Define"
      title="Workflows"
      lede="A workflow is any real thing — done by a person or an agent — whose success you do not want to take on trust. Connecting one means naming what must be true and the independent source that can confirm it."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <ScopeTabs
            value={scope}
            onChange={setScope}
            options={[
              { key: "admitted", label: `Admitted (${admitted.length})`, actionId: ACTION.scopeAdmitted },
              { key: "waiting", label: `Waiting (${pending.length})`, actionId: ACTION.scopeWaiting },
              { key: "rejected", label: `Not admitted (${rejected.length})`, actionId: ACTION.scopeRejected },
            ]}
          />
          <PageActions
            menu={
              <ActionMenu
                actionId="workflows.more"
                items={[
                  {
                    label: "Paste intake",
                    actionId: "workflows.open-intake",
                    disabled: connectDisabled,
                    reason: connectTitle,
                    onClick: () => onNavigate(href("workflows", "new")),
                  },
                ]}
              />
            }
            primary={
              <Btn
                actionId={ACTION.connect}
                variant="primary"
                disabled={connectDisabled}
                title={connectTitle}
                onClick={() => {
                  if (connectDisabled) return;
                  setComposing(true);
                }}
              >
                Connect a workflow
              </Btn>
            }
          />
        </div>
      }
    >
      {workflowsApi.error ? (
        <div className="vp-callout" data-tone="fail">
          <div className="vp-callout-title">Workflows unavailable</div>
          <div className="vp-callout-body">{errorMessage(workflowsApi.error)}</div>
        </div>
      ) : null}
      {notice ? (
        <div className="vp-callout" data-tone="fail">
          <div className="vp-callout-title">Admission refused</div>
          <div className="vp-callout-body">{notice}</div>
        </div>
      ) : null}
      {reason ? <GoldenVectorReasonCard finding={reasonFinding} reason={reason} /> : null}
      <RequiredStatusRenderingStrip />

      <div className="vp-stack" data-gap="sm">
        {list.map((c) => (
          <CandidateCard
            key={c.id}
            candidate={c}
            onNavigate={onNavigate}
            canMutate={scope === "waiting" && Boolean(environment)}
            onAdmit={async () => {
              if (scope !== "waiting") return;
              const result = await admitWorkflow(c.id);
              if (!result.ok) {
                setNotice(
                  result.reason === "oracle-not-independent"
                    ? "Cannot admit: this workflow has no independent oracle."
                    : `Cannot admit (${result.reason}).`,
                );
                return;
              }
              setNotice(null);
            }}
          />
        ))}
        {list.length === 0 ? <PagePending title={empty.title}>{empty.body}</PagePending> : null}
      </div>

      {composing && environment ? (
        <ConnectWorkflowDialog
          onClose={() => setComposing(false)}
          onCreated={(id) => {
            setComposing(false);
            onNavigate(workflowHref(id, environment));
          }}
        />
      ) : null}
      {!environment ? (
        <PagePending title="Environment unavailable">The active Environment must load before workflows can mutate.</PagePending>
      ) : null}
    </PageFrame>
  );
}

function CandidateCard({
  candidate,
  onNavigate,
  onAdmit,
  canMutate,
}: {
  candidate: WorkflowRow;
  onNavigate: (to: string) => void;
  onAdmit: () => void;
  canMutate: boolean;
}) {
  const independent = intakeOracleIsIndependent(candidate.oracle);
  const pending = candidate.status === "draft" || candidate.status === "needs-oracle";
  return (
    <div className="vp-card">
      <div className="vp-card-head">
        <div>
          <div className="vp-card-title">{candidate.name}</div>
          <div className="vp-card-sub">
            <span className="vp-mono">{candidate.id}</span>
          </div>
        </div>
        <div className="vp-card-head-actions">
          <ToneBadge tone={STATUS_TONE[candidate.status] ?? "neutral"}>
            {STATUS_LABEL[candidate.status] ?? candidate.status}
          </ToneBadge>
        </div>
      </div>
      <div className="vp-card-body">
        <div className="vp-fields">
          <div className="vp-field">
            <div className="vp-field-label">Who does it</div>
            <div className="vp-field-value">{candidate.actor}</div>
          </div>
          <div className="vp-field">
            <div className="vp-field-label">Independent oracle</div>
            <div className="vp-field-value">
              {candidate.oracle ? (
                <span>
                  <ToneBadge tone={independent ? "pass" : "fail"}>
                    {independent ? "present" : "not independent"}
                  </ToneBadge>{" "}
                  {candidate.oracle}
                </span>
              ) : (
                <ToneBadge tone="fail">none named</ToneBadge>
              )}
            </div>
          </div>
        </div>
        {candidate.rejectionReason ? (
          <div className="vp-callout" data-tone={candidate.status === "needs-oracle" ? "fail" : "neutral"}>
            <div className="vp-callout-title">Why this is not admitted</div>
            <div className="vp-callout-body">{candidate.rejectionReason}</div>
          </div>
        ) : null}
      </div>
      <div className="vp-card-foot">
        <Btn actionId={ACTION.detail} variant="ghost" size="sm" onClick={() => onNavigate(workflowHref(candidate.id, environmentOfId(candidate.id) === "real" ? "real" : "demo"))}>
          Detail
        </Btn>
        {pending ? (
          <Btn
            actionId={ACTION.listAdmit}
            variant={independent ? "primary" : "outline"}
            size="sm"
            disabled={!canMutate}
            title={
              !canMutate
                ? "Load the active Environment first"
                : independent
                  ? "Admit this workflow into verification"
                  : "Blocked — no independent oracle"
            }
            onClick={onAdmit}
          >
            Admit
          </Btn>
        ) : null}
      </div>
    </div>
  );
}

export function WorkflowDetailPage({ id, onNavigate }: VerificationDetailPageProps) {
  const detailEnvironment = environmentFromWorkflowId(id);
  const { environment, ready, workflowsApi, workflows, admitWorkflow, bindOracle } = useWorkflowActions(detailEnvironment);
  const [bindError, setBindError] = useState<string | null>(null);
  const [oracleDraft, setOracleDraft] = useState("");
  const decoded = decodeURIComponent(id);
  const resultParam = typeof window === "undefined"
    ? null
    : new URLSearchParams(window.location.search).get("result");
  const resultRecordId = resultParam && /^[1-9]\d*$/.test(resultParam)
    ? Number(resultParam)
    : null;
  const exactResultRecordId = resultRecordId != null && Number.isSafeInteger(resultRecordId)
    ? resultRecordId
    : null;
  // #47029: detail is fetched through the canonical single-workflow contract,
  // not derived by filtering the list response.
  const workflowGet = useApi(
    verificationCanonicalClients.workflowsGet.operation,
    ready && environment ? { params: { id: decoded }, query: { env: environment } } : null,
  );
  const checkActivity = useApi(
    activity.list,
    ready && environment && exactResultRecordId == null
      ? { query: { env: environment, category: "checks" } }
      : null,
  );
  const c = workflowGet.data?.workflow ?? workflows.find((row) => row.id === decoded || row.id.endsWith(decoded));

  if (!ready || (workflowsApi.isLoading && !workflowsApi.data) || (workflowGet.isLoading && !workflowGet.data)) {
    return (
      <PageFrame page="workflow" eyebrow="Define" title="Workflow">
        <PagePending title={`Loading ${id}`}>Fetching this workflow from verificationCanonicalClients.workflowsGet.operation.</PagePending>
      </PageFrame>
    );
  }

  if (!c) {
    return (
      <PageFrame page="workflow" eyebrow="Define" title="Workflow">
        <PagePending title={`No workflow loaded for ${id}`}>
          Workflow detail is where an oracle is named and admission is decided.{" "}
          <PageLink onNavigate={onNavigate} to="workflows">
            Back to workflows
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }

  const independent = intakeOracleIsIndependent(c.oracle);
  const pending = c.status === "draft" || c.status === "needs-oracle";
  const proofBasis = parseWorkflowProofBasis(c.evidence);
  const workflowPath = `/workflows/${encodeURIComponent(c.id)}`;
  const latestCheck = checkActivity.data?.groups
    .flatMap((group) => group.members)
    .filter((member) => member.href.split("?")[0] === workflowPath)
    .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))[0] ?? null;

  return (
    <PageFrame
      page="workflow"
      eyebrow={`Workflow · ${STATUS_LABEL[c.status] ?? c.status}`}
      title={c.name}
      lede="The admission record: what must be true, the independent oracle, and whether this candidate is admitted, draft, or still needs an oracle."
      actions={
        c.status === "admitted" ? (
          <Btn
            actionId={ACTION.openControl}
            variant="primary"
            onClick={() => onNavigate(href("controls", c.admittedControlId ?? "ctl_cc81"))}
          >
            Open the control
          </Btn>
        ) : (
          <Btn
            actionId={ACTION.admit}
            variant="primary"
            disabled={!environment}
            title={environment ? "Admit this workflow" : "Load the active Environment first"}
            onClick={async () => {
              const result = await admitWorkflow(c.id);
              setBindError(
                result.ok
                  ? null
                  : result.reason === "oracle-not-independent"
                    ? "Cannot admit: this workflow has no independent oracle."
                    : `Cannot admit (${result.reason}).`,
              );
            }}
          >
            Admit
          </Btn>
        )
      }
    >
      {workflowsApi.error ? (
        <div className="vp-callout" data-tone="fail">
          <div className="vp-callout-title">Workflow unavailable</div>
          <div className="vp-callout-body">{errorMessage(workflowsApi.error)}</div>
        </div>
      ) : null}
      {bindError ? (
        <div className="vp-callout" data-tone="fail">
          <div className="vp-callout-title">Action refused</div>
          <div className="vp-callout-body">{bindError}</div>
        </div>
      ) : null}

      <div className="vp-card">
        <div className="vp-card-head">
          <div className="vp-card-title">The oracle test</div>
        </div>
        <div className="vp-card-body">
          {c.oracle && independent ? (
            <div className="vp-callout" data-tone="pass">
              <div className="vp-callout-title">Passes: the oracle is independent of the actor.</div>
              <div className="vp-callout-body">{c.oracle}</div>
            </div>
          ) : (
            <div className="vp-callout" data-tone="fail">
              <div className="vp-callout-title">Fails: no source independent of the actor.</div>
              <div className="vp-callout-body">
                {c.rejectionReason ?? "Name something the actor does not write."}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="vp-card" data-workflow-proof-view="true">
        <div className="vp-card-head">
          <div>
            <div className="vp-card-title">Retained basis</div>
            <div className="vp-card-sub">Stored evidence and any linked named check.</div>
          </div>
        </div>
        <div className="vp-card-body">
          {proofBasis ? (
            <div className="vp-fields">
              <div className="vp-field">
                <div className="vp-field-label">Observed basis</div>
                <div className="vp-field-value">{proofBasis.observation}</div>
              </div>
              <div className="vp-field">
                <div className="vp-field-label">Observation review</div>
                <div className="vp-field-value vp-mono">{proofBasis.observationReviewId}</div>
              </div>
              <div className="vp-field">
                <div className="vp-field-label">Confirmed project</div>
                <div className="vp-field-value">
                  {proofBasis.project} <span className="vp-mono">({proofBasis.projectRoot})</span>
                </div>
              </div>
              <div className="vp-field">
                <div className="vp-field-label">Named check</div>
                <div className="vp-field-value vp-mono">{proofBasis.check}</div>
              </div>
            </div>
          ) : (
            <div className="vp-field">
              <div className="vp-field-label">Retained evidence</div>
              <div className="vp-field-value">{c.evidence}</div>
            </div>
          )}

          {exactResultRecordId != null ? (
            <CheckResultProof resultRecordId={exactResultRecordId} workflowId={c.id} />
          ) : (
            <>
              <div className="vp-callout" data-tone={latestCheck ? "neutral" : "unknown"}>
                <div className="vp-callout-title">
                  {checkActivity.isLoading && !checkActivity.data
                    ? "Checking for a linked result"
                    : checkActivity.error
                      ? "Check activity unavailable"
                      : latestCheck
                        ? "Latest linked check result"
                        : "No linked check result"}
                </div>
                <div className="vp-callout-body">
                  {checkActivity.isLoading && !checkActivity.data
                    ? "Reading this environment's check activity."
                    : checkActivity.error
                      ? "Excellent could not read check activity, so no outcome is shown."
                      : latestCheck
                        ? `${latestCheck.label ?? "A check result was recorded"} · ${formatDateTime(latestCheck.occurred_at)}`
                        : "No linked check result is available in the current Activity view."}
                </div>
              </div>

              <div className="vp-callout" data-tone="unknown">
                <div className="vp-callout-title">No signed receipt</div>
                <div className="vp-callout-body">
                  The retained basis and Activity result are not a signed, independently replayable receipt.
                </div>
              </div>
            </>
          )}
        </div>
        <div className="vp-card-foot">
          <Btn actionId="workflow.open-check-activity" variant="ghost" size="sm" onClick={() => onNavigate("/activity?view=checks")}>
            Open check activity
          </Btn>
        </div>
      </div>

      {pending ? (
        <div className="vp-card">
          <div className="vp-card-head">
            <div className="vp-card-title">Bind an independent oracle</div>
          </div>
          <div className="vp-card-body">
            <label className="vp-label" htmlFor="wf-bind-oracle">
              Name the oracle
            </label>
            <input
              id="wf-bind-oracle"
              className="vp-input"
              data-action-id={ACTION.oracleDetail}
              value={oracleDraft}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setOracleDraft(e.target.value)}
              placeholder='e.g. Stripe API — refund.status == "succeeded"'
              disabled
              title={BIND_PREREQ}
            />
            <div className="flex flex-wrap items-center gap-2" style={{ marginTop: "calc(var(--spacing) * 3)" }}>
              <Btn
                actionId={ACTION.bind}
                variant="primary"
                disabled
                title={BIND_PREREQ}
                onClick={() => {
                  const result = bindOracle(c.id, oracleDraft);
                  setBindError(result.ok ? null : `Cannot bind (${result.reason}).`);
                }}
              >
                Bind this oracle
              </Btn>
              <Btn
                actionId="workflow.bind-setup"
                variant="outline"
                onClick={() => onNavigate(href("workflows", "new"))}
              >
                Route to intake setup
              </Btn>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Btn actionId={ACTION.back} variant="ghost" onClick={() => onNavigate(href("workflows"))}>
          Back to workflows
        </Btn>
      </div>
    </PageFrame>
  );
}

function ConnectWorkflowDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { createWorkflow, admitWorkflow } = useWorkflowActions();
  const [name, setName] = useState("");
  const [who, setWho] = useState("");
  const [oracleKind, setOracleKind] = useState<"external" | "self" | null>(null);
  const [oracle, setOracle] = useState("");
  const [error, setError] = useState<string | null>(null);

  const independent = oracleKind === "external" && intakeOracleIsIndependent(oracle);
  const canSubmit = Boolean(name.trim() && who.trim() && independent);

  const submit = async () => {
    if (!canSubmit) return;
    const created = await createWorkflow({
      name: name.trim(),
      actor: who.trim(),
      oracle: oracle.trim(),
      evidence: oracle.trim(),
    });
    if (!created.ok || !created.createdId) {
      setError(created.ok ? "Create did not return an id." : `Cannot create (${created.reason}).`);
      return;
    }
    if (created.workflow.status === "admitted") {
      onCreated(created.createdId);
      return;
    }
    const admitted = await admitWorkflow(created.createdId);
    if (!admitted.ok) {
      setError(`Created but not admitted (${admitted.reason}).`);
      onCreated(created.createdId);
      return;
    }
    onCreated(created.createdId);
  };

  return (
    <div className="vp-scrim" role="presentation" onClick={onClose}>
      <div
        className="vp-dialog"
        data-size="lg"
        data-action-id="define.cancel-pages-define-l403"
        role="dialog"
        aria-labelledby="wf-connect-title"
        onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        <div className="vp-dialog-head">
          <h2 className="vp-dialog-title" id="wf-connect-title">
            Connect a workflow
          </h2>
          <p className="vp-dialog-sub">
            Four questions. The third is the one that decides whether this can be verified at all.
          </p>
        </div>
        <div className="vp-dialog-body">
          {error ? (
            <div className="vp-callout" data-tone="fail">
              <div className="vp-callout-title">Create refused</div>
              <div className="vp-callout-body">{error}</div>
            </div>
          ) : null}
          <div className="vp-form">
            <div>
              <label className="vp-label" htmlFor="wf-name">
                What happens that needs verifying?<span className="vp-req">*</span>
              </label>
              <input
                id="wf-name"
                className="vp-input"
                data-action-id={ACTION.name}
                value={name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                placeholder="e.g. A refund above $5,000 was actually issued"
              />
            </div>
            <div>
              <label className="vp-label" htmlFor="wf-actor">
                Who or what does it?<span className="vp-req">*</span>
              </label>
              <input
                id="wf-actor"
                className="vp-input"
                data-action-id={ACTION.actor}
                value={who}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setWho(e.target.value)}
                placeholder="e.g. billing-agent (automated)"
              />
            </div>
            <div>
              <div className="vp-label">
                What can confirm it, independently?<span className="vp-req">*</span>
              </div>
              <button
                type="button"
                className="vp-choice"
                data-selected={oracleKind === "external" ? "true" : undefined}
                data-action-id={ACTION.oracleExternal}
                onClick={() => setOracleKind("external")}
              >
                <span className="vp-choice-mark" aria-hidden="true" />
                <span>
                  <span className="vp-choice-title">An external system the actor does not control</span>
                  <span className="vp-choice-desc">This is the only answer that can be admitted.</span>
                </span>
              </button>
              <button
                type="button"
                className="vp-choice"
                data-selected={oracleKind === "self" ? "true" : undefined}
                data-action-id={ACTION.oracleSelf}
                onClick={() => setOracleKind("self")}
                style={{ marginTop: "calc(var(--spacing) * 2)" }}
              >
                <span className="vp-choice-mark" aria-hidden="true" />
                <span>
                  <span className="vp-choice-title">The actor's own report of what it did</span>
                  <span className="vp-choice-desc">An agent's summary cannot be admitted.</span>
                </span>
              </button>
              {oracleKind === "self" ? (
                <div className="vp-callout" data-tone="fail" style={{ marginTop: "calc(var(--spacing) * 2.5)" }}>
                  <div className="vp-callout-title">This cannot be verified.</div>
                  <div className="vp-callout-body">
                    The workflow stays in <span className="vp-mono">needs-oracle</span> until an independent
                    source is named.
                  </div>
                </div>
              ) : null}
              {oracleKind === "external" ? (
                <div style={{ marginTop: "calc(var(--spacing) * 2.5)" }}>
                  <label className="vp-label" htmlFor="wf-oracle-detail">
                    Name the oracle
                  </label>
                  <input
                    id="wf-oracle-detail"
                    className="vp-input"
                    data-action-id={ACTION.oracleDetail}
                    value={oracle}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setOracle(e.target.value)}
                    placeholder='e.g. Stripe API — refund.status == "succeeded"'
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="vp-dialog-foot">
          <Btn actionId={ACTION.cancel} variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            actionId={ACTION.createAdmit}
            variant="primary"
            disabled={!canSubmit}
            title={canSubmit ? "Create and admit" : "Name an independent oracle before creating"}
            onClick={() => void submit()}
          >
            Create and admit
          </Btn>
        </div>
      </div>
    </div>
  );
}
